/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import type {
  AccountMeta,
  AccountSignerMeta,
  Address,
  FixedSizeCodec,
  FixedSizeDecoder,
  FixedSizeEncoder,
  Instruction,
  InstructionWithAccounts,
  InstructionWithData,
  ReadonlyAccount,
  ReadonlySignerAccount,
  ReadonlyUint8Array,
  TransactionSigner,
  WritableAccount,
} from "@solana/kit";
import type { ResolvedInstructionAccount } from "@solana/program-client-core";
import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  getU8Decoder,
  getU8Encoder,
  getU128Decoder,
  getU128Encoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
} from "@solana/kit";
import { getAccountMetaFactory } from "@solana/program-client-core";
import { WHIRLPOOL_PROGRAM_ADDRESS } from "../programs/index.js";

export const SET_REWARD_EMISSIONS_V2_DISCRIMINATOR: ReadonlyUint8Array =
  new Uint8Array([114, 228, 72, 32, 193, 48, 160, 102]);

export function getSetRewardEmissionsV2DiscriminatorBytes(): ReadonlyUint8Array {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    SET_REWARD_EMISSIONS_V2_DISCRIMINATOR,
  );
}

export type SetRewardEmissionsV2Instruction<
  TProgram extends string = typeof WHIRLPOOL_PROGRAM_ADDRESS,
  TAccountWhirlpool extends string | AccountMeta = string,
  TAccountRewardAuthority extends string | AccountMeta = string,
  TAccountRewardVault extends string | AccountMeta = string,
  TRemainingAccounts extends readonly AccountMeta[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountWhirlpool extends string
        ? WritableAccount<TAccountWhirlpool>
        : TAccountWhirlpool,
      TAccountRewardAuthority extends string
        ? ReadonlySignerAccount<TAccountRewardAuthority> &
            AccountSignerMeta<TAccountRewardAuthority>
        : TAccountRewardAuthority,
      TAccountRewardVault extends string
        ? ReadonlyAccount<TAccountRewardVault>
        : TAccountRewardVault,
      ...TRemainingAccounts,
    ]
  >;

export interface SetRewardEmissionsV2InstructionData {
  discriminator: ReadonlyUint8Array;
  rewardIndex: number;
  emissionsPerSecondX64: bigint;
}

export interface SetRewardEmissionsV2InstructionDataArgs {
  rewardIndex: number;
  emissionsPerSecondX64: number | bigint;
}

export function getSetRewardEmissionsV2InstructionDataEncoder(): FixedSizeEncoder<SetRewardEmissionsV2InstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["rewardIndex", getU8Encoder()],
      ["emissionsPerSecondX64", getU128Encoder()],
    ]),
    (value) => ({
      ...value,
      discriminator: SET_REWARD_EMISSIONS_V2_DISCRIMINATOR,
    }),
  );
}

export function getSetRewardEmissionsV2InstructionDataDecoder(): FixedSizeDecoder<SetRewardEmissionsV2InstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["rewardIndex", getU8Decoder()],
    ["emissionsPerSecondX64", getU128Decoder()],
  ]);
}

export function getSetRewardEmissionsV2InstructionDataCodec(): FixedSizeCodec<
  SetRewardEmissionsV2InstructionDataArgs,
  SetRewardEmissionsV2InstructionData
> {
  return combineCodec(
    getSetRewardEmissionsV2InstructionDataEncoder(),
    getSetRewardEmissionsV2InstructionDataDecoder(),
  );
}

export interface SetRewardEmissionsV2Input<
  TAccountWhirlpool extends string = string,
  TAccountRewardAuthority extends string = string,
  TAccountRewardVault extends string = string,
> {
  whirlpool: Address<TAccountWhirlpool>;
  rewardAuthority: TransactionSigner<TAccountRewardAuthority>;
  rewardVault: Address<TAccountRewardVault>;
  rewardIndex: SetRewardEmissionsV2InstructionDataArgs["rewardIndex"];
  emissionsPerSecondX64: SetRewardEmissionsV2InstructionDataArgs["emissionsPerSecondX64"];
}

export function getSetRewardEmissionsV2Instruction<
  TAccountWhirlpool extends string,
  TAccountRewardAuthority extends string,
  TAccountRewardVault extends string,
  TProgramAddress extends Address = typeof WHIRLPOOL_PROGRAM_ADDRESS,
>(
  input: SetRewardEmissionsV2Input<
    TAccountWhirlpool,
    TAccountRewardAuthority,
    TAccountRewardVault
  >,
  config?: { programAddress?: TProgramAddress },
): SetRewardEmissionsV2Instruction<
  TProgramAddress,
  TAccountWhirlpool,
  TAccountRewardAuthority,
  TAccountRewardVault
> {
  // Program address.
  const programAddress = config?.programAddress ?? WHIRLPOOL_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    whirlpool: { value: input.whirlpool ?? null, isWritable: true },
    rewardAuthority: {
      value: input.rewardAuthority ?? null,
      isWritable: false,
    },
    rewardVault: { value: input.rewardVault ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta("whirlpool", accounts.whirlpool),
      getAccountMeta("rewardAuthority", accounts.rewardAuthority),
      getAccountMeta("rewardVault", accounts.rewardVault),
    ],
    data: getSetRewardEmissionsV2InstructionDataEncoder().encode(
      args as SetRewardEmissionsV2InstructionDataArgs,
    ),
    programAddress,
  } as SetRewardEmissionsV2Instruction<
    TProgramAddress,
    TAccountWhirlpool,
    TAccountRewardAuthority,
    TAccountRewardVault
  >);
}

export interface ParsedSetRewardEmissionsV2Instruction<
  TProgram extends string = typeof WHIRLPOOL_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> {
  programAddress: Address<TProgram>;
  accounts: {
    whirlpool: TAccountMetas[0];
    rewardAuthority: TAccountMetas[1];
    rewardVault: TAccountMetas[2];
  };
  data: SetRewardEmissionsV2InstructionData;
}

export function parseSetRewardEmissionsV2Instruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedSetRewardEmissionsV2Instruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 3) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 3,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      whirlpool: getNextAccount(),
      rewardAuthority: getNextAccount(),
      rewardVault: getNextAccount(),
    },
    data: getSetRewardEmissionsV2InstructionDataDecoder().decode(
      instruction.data,
    ),
  };
}
